import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

// Note: the JWT lives primarily in an httpOnly cookie set by the backend.
// The localStorage copy is only a fallback for direct file/PDF links (?auth=) in cross-site preview contexts.
const remember = (token) => { if (token) localStorage.setItem("access_token", token); };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((res) => setUser(res.data)).catch(() => setUser(false)).finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    remember(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    remember(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const setSession = useCallback((data) => {
    remember(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, setUser, setSession, ready, login, register, logout }), [user, ready, login, register, logout, setSession]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
